import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, theme, Modal, Button, Badge, Spin, message } from 'antd';
import {
  HomeOutlined,
  SearchOutlined,
  GlobalOutlined,
  UserOutlined,
  SafetyOutlined,
  ShopOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ReloadOutlined,
  BankOutlined,
  FlagOutlined,
  TagsOutlined,
  LockOutlined,
  ToolOutlined,
  SafetyCertificateOutlined,
  CalculatorOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import { useState, useCallback } from 'react';
import { useWisecureQuery, useInvalidateQueries } from '@wisecure/api-client/hooks';
import type { PendingValidationsResponse, SecurityRequestCountsResponse } from '@wisecure/api-client';
import type { PaginatedResponse } from '@/types/dashboard';
import { useAuthStore, useUIStore } from '@/stores';
import { queryKeys } from '@/api/query-keys';
import { queryClient } from '@/api/client';
import { neutral } from '@/theme';
import { APP_VERSION } from '@/version';

const { Header, Sider, Content } = Layout;

export function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useAuthStore((s) => s.logout);
  const user = useAuthStore((s) => s.user);
  const { sidebarCollapsed, toggleSidebar } = useUIStore();
  const invalidate = useInvalidateQueries();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const [refreshing, setRefreshing] = useState(false);

  const { data: pendingData, isLoading: pendingLoading } =
    useWisecureQuery<PendingValidationsResponse>(
      queryKeys.dashboardPendingValidations,
      (client) => client.dashboard.dashboardControllerGetPendingValidations(),
    );

  const pendingAgentsCount = pendingData?.pendingAgents?.length ?? 0;
  const pendingSocietiesCount = pendingData?.pendingSocieties?.length ?? 0;

  const { data: pendingReports, isLoading: pendingReportsLoading } =
    useWisecureQuery<PaginatedResponse<unknown>>(
      queryKeys.contentReportsPendingCount,
      (client) =>
        client.contentReports.contentReportsControllerFindAll({
          status: 'PENDING',
          limit: 1,
        }) as Promise<PaginatedResponse<unknown>>,
    );

  const pendingReportsCount = pendingReports?.total ?? 0;

  const { data: securityCounts, isLoading: securityCountsLoading } =
    useWisecureQuery<SecurityRequestCountsResponse>(
      queryKeys.securityRequestCounts,
      (client) => client.dashboardSecurity.securityDashboardControllerCounts(),
    );

  const pendingSecurityRequestsCount = securityCounts?.pending ?? 0;

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    invalidate(queryKeys.dashboardMetrics);
    invalidate(queryKeys.dashboardPendingValidations);
    invalidate(queryKeys.dashboardUsers);
    invalidate(queryKeys.dashboardSocieties);
    invalidate(queryKeys.dashboardSectorMap);
    invalidate(queryKeys.contentReportsPendingCount);
    invalidate(queryKeys.securityRequestCounts);
    invalidate(queryKeys.securityRequestsAll);
    message.success('Données actualisées');
    setTimeout(() => setRefreshing(false), 5000);
  }, [invalidate]);

  const handleLogout = () => {
    Modal.confirm({
      title: 'Déconnexion',
      content: 'Êtes-vous sûr de vouloir vous déconnecter ?',
      okText: 'Oui',
      cancelText: 'Non',
      onOk: () => {
        logout();
        queryClient.clear();
        navigate('/login');
      },
    });
  };

  const pendingBadge = (count: number, loading?: boolean) =>
    loading ?? pendingLoading ? (
      <Spin size="small" style={{ marginLeft: 8 }} />
    ) : count > 0 ? (
      <Badge count={count} size="small" style={{ marginLeft: 8 }} />
    ) : null;

  const menuItems = [
    { key: '/', icon: <HomeOutlined />, label: 'Accueil' },
    { key: '/search', icon: <SearchOutlined />, label: 'Recherche' },
    { key: '/sectors', icon: <GlobalOutlined />, label: 'Secteurs' },
    { key: '/municipalities', icon: <BankOutlined />, label: 'Municipalités' },
    { key: '/interventions', icon: <ToolOutlined />, label: 'Interventions' },
    {
      key: '/moderation',
      icon: <FlagOutlined />,
      label: (
        <span>
          Modération
          {pendingBadge(pendingReportsCount, pendingReportsLoading)}
        </span>
      ),
    },
    { key: '/feed', icon: <BankOutlined />, label: "Fil d'actualités" },
    {
      key: '/users/validation',
      icon: <SafetyOutlined />,
      label: (
        <span>
          Validation des agents
          {pendingBadge(pendingAgentsCount)}
        </span>
      ),
    },
    {
      key: '/societies/validation',
      icon: <ShopOutlined />,
      label: (
        <span>
          Validation des sociétés
          {pendingBadge(pendingSocietiesCount)}
        </span>
      ),
    },
    {
      key: '/security-requests',
      icon: <SafetyCertificateOutlined />,
      // Badge in `extra` and an ellipsed label, so the count stays visible in the narrow sidebar.
      label: (
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          Demandes sécurité
        </span>
      ),
      extra: pendingBadge(pendingSecurityRequestsCount, securityCountsLoading),
    },
    { type: 'divider' as const },
    { key: '/security-catalog', icon: <CalculatorOutlined />, label: 'Catalogue sécurité' },
    { key: '/brands', icon: <TagsOutlined />, label: 'Marques' },
    { key: '/security-domains', icon: <LockOutlined />, label: 'Domaines sécurité' },
    { type: 'divider' as const },
    { key: '/feature-flags', icon: <ExperimentOutlined />, label: 'Feature flags' },
    { key: '/settings', icon: <SettingOutlined />, label: "Réglage de l'application" },
    { type: 'divider' as const },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Déconnexion', danger: true },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider trigger={null} collapsible collapsed={sidebarCollapsed} breakpoint="lg" onBreakpoint={(broken) => { if (broken && !sidebarCollapsed) toggleSidebar(); }} style={{ position: 'relative' }}>
        <div style={{ height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: neutral[0], fontWeight: 700, fontSize: sidebarCollapsed ? 14 : 18 }}>
            {sidebarCollapsed ? 'WS' : 'WiSecure'}
          </span>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[
            location.pathname.startsWith('/security-requests/') ? '/security-requests' : location.pathname,
          ]}
          items={menuItems}
          onClick={({ key }) => {
            if (key === 'logout') {
              handleLogout();
            } else {
              navigate(key);
            }
          }}
        />
        <div style={{ position: 'absolute', bottom: 16, width: '100%', textAlign: 'center' }}>
          <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>
            v{APP_VERSION}
          </span>
        </div>
      </Sider>
      <Layout>
        <Header
          style={{
            padding: '0 24px',
            background: colorBgContainer,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span onClick={toggleSidebar} style={{ cursor: 'pointer', fontSize: 18 }}>
            {sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={refreshing}
              disabled={refreshing}
            >
              Rafraîchir
            </Button>
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'logout',
                    icon: <LogoutOutlined />,
                    label: 'Déconnexion',
                    onClick: handleLogout,
                  },
                ],
              }}
              placement="bottomRight"
            >
              <span style={{ cursor: 'pointer' }}>
                <Avatar icon={<UserOutlined />} />{' '}
                {[user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email}
              </span>
            </Dropdown>
          </div>
        </Header>
        <Content style={{ margin: 24, padding: 24, background: colorBgContainer, borderRadius: borderRadiusLG }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
